import { useState } from 'react';

import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import legacyTheme from '../../../theme';
import { tokens } from '../../../theme-next';
import type { TaxData } from '../../../types/cg';
import { useFormCGIAStore } from '../store/useFormCGIAStore';
import { REGPLATE_REGEX, normalizePlate } from './plateDept';
import { SectionCard } from './PremiumComponents';

/**
 * Étape 1 du flow FormCGIA v2 (inversion des étapes — voir plan).
 *
 * L'agence saisit l'immatriculation + le département AVANT de déposer la
 * moindre pièce. Au submit on appelle `/api/misiv/ecg` pour obtenir le
 * calcul de taxe ; le résultat est stocké dans `taxData` du store et
 * utilisé en étape 2 (TaxConfirmationDialog) pour afficher le prix.
 *
 * UI alignée sur le récap legacy de FormCGIA v2 (cf. RecapAgencyScreen
 * d'origine) : header avec overline + titre 38 px + sub-description, puis
 * SectionCard à liseré jaune avec une grid 2fr/1fr pour Immat / Département.
 */

export const PlateAndDepartmentScreen = () => {
  const setPlateAndDepartment = useFormCGIAStore((s) => s.setPlateAndDepartment);
  const setTaxData = useFormCGIAStore((s) => s.setTaxData);
  const setV2Step = useFormCGIAStore((s) => s.setV2Step);
  const setIsSuccession = useFormCGIAStore((s) => s.setIsSuccession);
  const setIsProfessionalResale = useFormCGIAStore((s) => s.setIsProfessionalResale);
  const storedPlate = useFormCGIAStore((s) => s.regPlate);
  const storedDept = useFormCGIAStore((s) => s.department);
  const storedSuccession = useFormCGIAStore((s) => s.isSuccession);
  const storedProfessionalResale = useFormCGIAStore((s) => s.isProfessionalResale);

  const [regPlate, setRegPlate] = useState<string>(storedPlate || '');
  const [department, setDepartment] = useState<string>(storedDept || '');
  const [succession, setSuccession] = useState<boolean>(storedSuccession);
  const [professionalResale, setProfessionalResale] = useState<boolean>(storedProfessionalResale);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const normalizedPlate = normalizePlate(regPlate);
    if (!REGPLATE_REGEX.test(normalizedPlate)) {
      setError("Format d'immatriculation invalide (attendu : AA-123-BB).");
      return;
    }
    const cleanDept = department.trim();
    if (!/^[0-9]{2,3}$/.test(cleanDept)) {
      setError('Département invalide (2 ou 3 chiffres).');
      return;
    }

    setLoading(true);
    try {
      const resp = await makeAuthenticatedApiCall<TaxData>('post', '/api/misiv/ecg', {
        regPlate: normalizedPlate,
        department: cleanDept,
      });
      if (!resp.data || typeof resp.data.taxeTotale !== 'number') {
        throw new Error('Réponse ECG invalide');
      }
      setPlateAndDepartment(normalizedPlate, cleanDept);
      setIsSuccession(succession);
      setIsProfessionalResale(professionalResale);
      setTaxData(resp.data as unknown as TaxData);
      setV2Step('tax_confirm');
    } catch (err: any) {
      const data = err?.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        data?.message ||
        data?.error ||
        err?.message ||
        'Impossible de calculer la taxe pour cette immatriculation.';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = regPlate.trim().length > 0 && department.trim().length > 0 && !loading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: tokens.color.yellow,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          Étape 1 / 3 · Identification du véhicule
        </Typography>
        <Typography
          sx={{
            fontSize: '38px',
            fontWeight: 900,
            color: tokens.color.dark,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
            marginTop: '6px',
          }}
        >
          Saisissez la plaque et le département
        </Typography>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 500,
            color: tokens.color.dark,
            marginTop: '10px',
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          Ces deux informations suffisent à calculer le coût du certificat. Le devis sera affiché à l'étape suivante
          avant de passer au dépôt des pièces.
        </Typography>
      </Box>

      {/* ── Form minimal plaque + département ────────────── */}
      <SectionCard
        title="Informations véhicule"
        subtitle="Renseignez l'immatriculation au format SIV et le département de l'acheteur"
        subtitleStrong
        accentColor={tokens.color.yellow}
      >
        <Box
          sx={{
            padding: '24px 26px',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: '24px 28px',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: tokens.color.textMuted,
                marginBottom: '8px',
              }}
            >
              Immatriculation
            </Typography>
            <TextField
              value={regPlate}
              onChange={(e) => setRegPlate(e.target.value.toUpperCase())}
              fullWidth
              autoFocus
              placeholder="AA-123-BB"
              disabled={loading}
              inputProps={{ maxLength: 10, style: { letterSpacing: '0.1em' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '22px',
                  fontWeight: 800,
                  '& input': {
                    padding: '14px',
                    textAlign: 'left',
                  },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                color: tokens.color.textMuted,
                marginTop: '6px',
              }}
            >
              Format SIV obligatoire — 2 lettres, 3 chiffres, 2 lettres (ex : AB-123-CD).
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: tokens.color.textMuted,
                marginBottom: '8px',
              }}
            >
              Département de l'acheteur
            </Typography>
            <TextField
              value={department}
              onChange={(e) => setDepartment(e.target.value.replace(/[^0-9]/g, ''))}
              fullWidth
              placeholder="75"
              disabled={loading}
              inputProps={{ maxLength: 3 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '22px',
                  fontWeight: 800,
                  '& input': { padding: '14px' },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                color: tokens.color.textMuted,
                marginTop: '6px',
              }}
            >
              2 ou 3 chiffres. Essentiel pour le calcul de la taxe régionale.
            </Typography>
          </Box>
        </Box>
      </SectionCard>

      {/* ── Type de dossier (déclaration agence) ───────────────
          Déclaré ICI plutôt que deviné par l'IA : pilote l'ajout des blocs de
          pièces supplémentaires dans la checklist Documents (récépissé DA pour
          le rachat pro / Parcours A ; acte de décès + justificatif de
          succession pour une succession). */}
      <SectionCard
        title="Type de dossier"
        subtitle="Cochez si ce dossier nécessite des pièces particulières"
        subtitleStrong
        accentColor={tokens.color.yellow}
      >
        <Box sx={{ padding: '18px 26px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={succession}
                onChange={(e) => setSuccession(e.target.checked)}
                disabled={loading}
              />
            }
            sx={{ alignItems: 'center', marginRight: 0 }}
            label={
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: tokens.color.dark }}>
                  Ce dossier est une succession
                </Typography>
                <Typography sx={{ fontSize: '13px', color: tokens.color.textMuted, lineHeight: 1.5 }}>
                  Décès du titulaire. Ajoute les pièces : acte de décès + justificatif de succession (acte de notoriété
                  ou attestation des héritiers).
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={professionalResale}
                onChange={(e) => setProfessionalResale(e.target.checked)}
                disabled={loading}
              />
            }
            sx={{ alignItems: 'center', marginRight: 0 }}
            label={
              <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: tokens.color.dark }}>
                  Ce dossier a été racheté par un professionnel
                </Typography>
                <Typography sx={{ fontSize: '13px', color: tokens.color.textMuted, lineHeight: 1.5 }}>
                  Parcours A. Ajoute la pièce : récépissé de déclaration d'achat (DA) du professionnel.
                </Typography>
              </Box>
            }
          />
        </Box>
      </SectionCard>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: '14px' }}>
          {error}
        </Alert>
      )}

      {/* ── CTA ────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ThemeProvider theme={legacyTheme}>
          <Button
            className="dealActionButton"
            onClick={handleSubmit}
            disabled={!canSubmit}
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? 'Calcul en cours…' : 'Calculer le prix →'}
          </Button>
        </ThemeProvider>
      </Box>
    </Box>
  );
};

export default PlateAndDepartmentScreen;
